import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SortIcon } from '@/components/common/SortIcon';
import { DeleteDialog } from '@/components/common/Dialogs';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { devicesQueryKeys } from '../api';
import { DeviceStatusBadge } from './DeviceStatusBadge';
import { ToggleStatusButton } from './ToggleStatusButton';
import type { Device, DeviceFilters } from '../types';

interface GetColumnsParams {
  orderBy: string;
  order: 'asc' | 'desc' | '';
  onSort: (field: string) => void;
  filters: DeviceFilters;
  onToggle?: (id: number, status: 'ACTIVE' | 'BLOCKED') => void;
  isTogglePending?: boolean;
}

export function useDeviceColumns({
  orderBy,
  order,
  onSort,
  onToggle,
  isTogglePending,
}: GetColumnsParams): ColumnDef<Device>[] {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);

  const columns: ColumnDef<Device>[] = [
    {
      accessorKey: 'model',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('model')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('model')}
        >
          <p>{t('model')}</p>
          <SortIcon active={orderBy === 'model'} direction={order} />
        </div>
      ),
      cell: ({ row }) => (
        <Link
          to={`/app/devices/${row.original.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original.model || '-'}
        </Link>
      ),
    },
    {
      accessorKey: 'status',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('status')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('status')}
        >
          <p>{t('status.status')}</p>
          <SortIcon active={orderBy === 'status'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <DeviceStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'version',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('version')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('version')}
        >
          <p>{t('version')}</p>
          <SortIcon active={orderBy === 'version'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <div>{row.original.version || '-'}</div>,
    },
    {
      accessorKey: 'owner',
      header: () => <p>{t('owner')}</p>,
      cell: ({ row }) => (
        <Link
          to={`/app/users/${row.original.user?.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original.user?.name || '-'}
        </Link>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer select-none"
          role="button"
          tabIndex={0}
          onClick={() => onSort('createdAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('createdAt')}
        >
          <p>{t('createdAt')}</p>
          <SortIcon active={orderBy === 'createdAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const dateStr = row.original.createdAt;
        return dateStr ? (
          <div>{format(new Date(dateStr), 'yyyy-MM-dd HH:mm')}</div>
        ) : (
          <span>-</span>
        );
      },
    },
  ];

  // Add actions column if user has permissions
  if (hasAccess('device', 'update') || hasAccess('device', 'delete')) {
    columns.splice(0, 0, {
      id: 'actions',
      header: t('actionsLabel'),
      cell: ({ row }) => {
        const data = row.original;

        return (
          <div className="flex">
            {hasAccess('device', 'update') && onToggle && (
              <ToggleStatusButton
                device={data}
                onToggle={onToggle}
                isPending={isTogglePending}
              />
            )}
            {hasAccess('device', 'delete') && (
              <DeleteDialog
                id={data.id}
                title={data.model || `Device #${data.id}`}
                url="devices"
                queryKey={devicesQueryKeys.lists()}
              />
            )}
          </div>
        );
      },
      enableHiding: false,
    });
  }

  return columns;
}
